import { Controller } from "@hotwired/stimulus"

const SETTINGS = {
  baseColor: '#ffffff',
  highlightColor: '#fff8d1'
}

export default class extends Controller {
  static targets = [
    'chassisDropOff',
    'chassisPickUp',
    'containerDropOff',
    'containerPickUp',
    'gateErrors',
    'gates',
    'gcDropOff',
    'gcPickUp',
    'outGateSection',
    'rowsContainer',
    'submit',
    'tbdButton',
  ]
  static outlets = [
    'truck-gate-in--truck-drivers',
    'truck-gate-in--trucks',
    'truck-gate-in--validations',
  ]

  connect() {
    this.index = Date.now() // Unique index for nested attributes

    if (this.hasOutGateSectionTarget) {
      // Hide the out gate section initially
      this.hideOutGateSection()
    }

    // Global form submission blockers
    this.blockers = new Set()
    this.updateSubmit()
  }

  lockSubmission(key) {
    this.blockers.add(key)
    this.updateSubmit()
  }

  unlockSubmission(key) {
    this.blockers.delete(key)
    this.updateSubmit()
  }

  updateSubmit() {
    if (this.hasSubmitTarget) {
      const disabled = this.blockers.size > 0
      this.submitTarget.disabled = disabled
      this.submitTarget.classList.toggle("disabled", disabled)
    }
  }

  addRow(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.formManagerTypeParam
    const templateTarget = this.getTemplateTarget(type)
    if (!templateTarget) return

    const content = templateTarget.content.cloneNode(true)
    const indexedContent = content.querySelector('.panel')
    const fullIndex = this.index++ // Unique index for full fields
    const emptyIndex = this.index++ // Unique index for empty fields
    // Set an initial highlight so it’s visible the moment we append
    const panelBody = indexedContent.querySelector('.panel-body')
    if (panelBody) {
      panelBody.style.backgroundColor = SETTINGS.highlightColor
    } else {
      indexedContent.style.backgroundColor = SETTINGS.highlightColor
    }
    indexedContent.innerHTML = indexedContent.innerHTML
      .replace(/FULL_INDEX/g, fullIndex)
      .replace(/EMPTY_INDEX/g, emptyIndex)
    this.rowsContainerTarget.appendChild(indexedContent)
    // Ensure the new row is brought into view and highlighted
    this.highlightAndScroll(indexedContent)

    // Check if this is a pick-up action and update the out gate section visibility
    this.updateOutGateSectionVisibility()
  }

  deleteRow(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-purpose-row]')
    row.remove()
    // After removing a row, check if we still need to show the out gate section
    this.updateOutGateSectionVisibility()
  }

  // Returns the template that matches a purpose row
  // key *only* if the corresponding target is present on the page.
  getTemplateTarget(type) {
    const templateMap = {};
    if (this.hasContainerDropOffTarget) templateMap.container_drop_off = this.containerDropOffTarget;
    if (this.hasContainerPickUpTarget)  templateMap.container_pick_up  = this.containerPickUpTarget;
    if (this.hasGcDropOffTarget)        templateMap.gc_drop_off        = this.gcDropOffTarget;
    if (this.hasGcPickUpTarget)         templateMap.gc_pick_up         = this.gcPickUpTarget;
    if (this.hasChassisDropOffTarget)   templateMap.chassis_drop_off   = this.chassisDropOffTarget;
    if (this.hasChassisPickUpTarget)    templateMap.chassis_pick_up    = this.chassisPickUpTarget;

    return templateMap[type]
  }

  changeGate() {
    if (this.hasTruckGateInValidationsOutlet) {
      this.truckGateInValidationsOutlets.forEach(panel =>
        panel.pickUpFullContainerChecks()
      )
    }

    // Gate selection validation
    if (!this.hasGateErrorsTarget) return

    if (this.#selectedGateElement()) {
      this.unlockSubmission('gateRequired')
      this.#renderGateErrors([])
    }
  }

  submitForm(event) {
    // Top‑level validators (these may add their own locks).
    this.validateGate(event);
    this.validateTruckPresence(event);
    this.validateTruckDriverPresence(event);

    // Per‑panel presence checks manage the "presence" lock internally.
    this.truckGateInValidationsOutlets.forEach((panel) => {
      panel.runPresenceChecks();
    });

    // If no locks remain we can safely submit the form.
    if (this.blockers.size === 0) this.element.requestSubmit()
  }

  /**
   * Intercepts form submission; if multiple gates are available and none
   * is selected we prevent submit, show the error, and lock the button.
   */
  validateGate(event) {
    const multiple = this.hasGateErrorsTarget && this.gatesTargets.filter(el => el.type !== 'hidden').length > 1
    if (multiple && !this.#selectedGateElement()) {
      event.preventDefault()
      this.lockSubmission('gateRequired')
      this.#renderGateErrors(['You must select a gate.'])
    }
  }

  validateTruckPresence(event) {
    if (!this.hasTruckGateInTrucksOutlet) return

    this.truckGateInTrucksOutlet.validatePresence(event)
  }

  validateTruckDriverPresence(event) {
    if (!this.hasTruckGateInTruckDriversOutlet) return

    this.truckGateInTruckDriversOutlet.validatePresence(event);
  }

  /**
   * Returns the DOM element representing the selected gate:
   * – the hidden input when there is only one active gate, or
   * – the checked radio button when multiple gates are shown.
   * Returns `null` if no gate element can be found.
   */
  #selectedGateElement() {
    if (!this.hasGatesTarget) return null
    const hidden = this.gatesTargets.find(el => el.type === "hidden")
    if (hidden) return hidden
    return this.gatesTargets.find(el => el.checked) || null
  }

  currentTerminalName() {
    const el = this.#selectedGateElement()
    return el ? (el.dataset.terminalName || "") : ""
  }

  currentGateName() {
    const el = this.#selectedGateElement()
    return el ? (el.dataset.gateName || "") : ""
  }

  #renderGateErrors(messages = []) {
    if (!this.hasGateErrorsTarget) return
    const html = messages.length
      ? `<div class="alert alert-danger" role="alert">
           <ul style="padding: 0 0 0 1em;">
             ${messages.map(m => `<li><p style="margin-bottom:0;">${m}</p></li>`).join('')}
           </ul>
         </div>`
      : ''

    if (this.gateErrorsTarget.dataset.prevHtml !== html) {
      this.gateErrorsTarget.dataset.prevHtml = html
      this.gateErrorsTarget.innerHTML = html
    }
  }

  toggleTbd(event) {
    event.preventDefault()
    const hiddenInput = document.getElementById('tbd_hidden')
    const isTbd = hiddenInput.value === 'true'

    if (!isTbd) {
      hiddenInput.value = 'true'
      this.rowsContainerTarget.innerHTML = ''
      const addButtons = this.element.querySelectorAll('button[data-action*="#addRow"]')
      addButtons.forEach(button => button.disabled = true)
      this.tbdButtonTarget.classList.add('btn-primary')
      // Hide out gate section when TBD is selected
      this.hideOutGateSection()
    } else {
      hiddenInput.value = 'false'
      const addButtons = this.element.querySelectorAll('button[data-action*="#addRow"]')
      addButtons.forEach(button => button.disabled = false)
      this.tbdButtonTarget.classList.remove('btn-primary')
      // Check if we need to show the out gate section
      this.updateOutGateSectionVisibility()
    }
  }

  updateOutGateSectionVisibility() {
    // Only proceed if we have the outGateSection target
    if (!this.hasOutGateSectionTarget) return

    // Check if any pick-up rows exist
    const hasPickUpRow = this.rowsContainerTarget.querySelectorAll('[data-purpose-type="pick_up"]').length > 0

    if (hasPickUpRow) {
      this.showOutGateSection()
    } else {
      this.hideOutGateSection()
    }
  }

  showOutGateSection() {
    if (this.hasOutGateSectionTarget) {
      this.outGateSectionTarget.style.display = 'block'
    }
  }

  hideOutGateSection() {
    if (this.hasOutGateSectionTarget) {
      // Clear all fields in the out gate section
      this.clearOutGateFields()
      this.outGateSectionTarget.style.display = 'none'
    }
  }

  clearOutGateFields() {
    if (this.hasOutGateSectionTarget) {
      this.outGateSectionTarget.querySelectorAll('input[type="radio"]').forEach(radio => {
        radio.checked = false;
        const changeEvent = new Event('change', { bubbles: true });
        radio.dispatchEvent(changeEvent);
      });
    }
  }

  // Smoothly scroll to a newly‑added element and briefly highlight it
  highlightAndScroll(element) {
    requestAnimationFrame(() => {
      // Scroll the new panel into view
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const panelBody = element.querySelector('.panel-body') || element
      // Web Animations API
      if (panelBody.animate) {
        panelBody.animate(
          [
            { backgroundColor: SETTINGS.highlightColor },
            { backgroundColor: SETTINGS.baseColor }
          ],
          {
            duration: 1200,
            easing: 'ease-out',
            fill: 'forwards'
          }
        )
      } else {
        // Fallback using CSS transition
        panelBody.style.backgroundColor = SETTINGS.highlightColor
        // Force reflow so transition is applied
        void panelBody.offsetHeight
        panelBody.style.transition = 'background-color 1s ease-out'
        panelBody.style.backgroundColor = SETTINGS.baseColor
      }
    })
  }
}
